import {
  Create,
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  required,
  FileInput,
  FileField,
  RadioButtonGroupInput,
  DateInput,
  useNotify,
  Toolbar,
  SaveButton,
} from "react-admin";
import { Alert } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useFormState } from "react-hook-form";
import {
  Card,
  CardContent,
  Box,
  Grid,
  Typography,
  Divider,
} from "@mui/material";
import ComponentEdit from "../../components/PageComponents/ComponentEdit";
import { useFormContext } from "react-hook-form";
import { useCurrentProject } from "../../contexts/AccountProjectContext";
import { useSyncProjectIdToForm } from "../../hooks/useSyncProjectIdToForm";
import { getData } from "country-list";
import { paymentCurrencyChoices } from "../../models/paymentCurrency";
import FormField from "../../components/Common/FormField";

const OrderFormFields = () => {
  const { t }: { t: any } = useTranslation();
  useSyncProjectIdToForm();
  const { name } = useCurrentProject();
  const { errors } = useFormState();
  const errorMessageProject = Boolean(errors?.projectId);

  return (
    <Grid container spacing={3}>
      {errorMessageProject && (
        <Grid item xs={12}>
          <Alert severity="error">
            Entity selection is not configured to create a crypto payment.
            Please select another entity on top of the page or update the
            configuration in the entity settings.
          </Alert>
        </Grid>
      )}
      <Grid item xs={12} sx={{ display: "none" }}>
        <TextInput source="projectId" label={name} fullWidth disabled />
      </Grid>

      <Grid item xs={12}>
        <Typography variant="overline" color="text.secondary">
          {t("Basic information")}
        </Typography>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormField label={t("Internal reference")}>
          <TextInput
            source="internalRef"
            label={t("Internal reference")}
            fullWidth
            validate={required()}
            helperText="Please enter the internal reference."
          />
        </FormField>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormField label={t("Euro amount")}>
          <NumberInput
            source="euroAmount"
            label={t("Euro amount")}
            fullWidth
            helperText="Please enter the amount in euros. For amounts above 1000€, customer information will be required."
          />
        </FormField>
      </Grid>

      <Grid item xs={12} md={6}>
        <FormField label={t("Currency")}>
          <SelectInput
            source="currency"
            label={t("Currency")}
            helperText="Please select the currency."
            fullWidth
            choices={paymentCurrencyChoices}
          />
        </FormField>
      </Grid>

      <CustomerFields />
      <DocumentFields />
    </Grid>
  );
};

const CustomerFields = () => {
  const { t }: { t: any } = useTranslation();
  const { watch } = useFormContext();
  const euroAmount = watch("euroAmount");
  const customerType = watch("customer.type");
  const countries = getData().map((country) => ({
    id: country.code,
    name: country.name,
  }));
  countries.push({
    id: "other",
    name: "Other",
  });
  if (!euroAmount || euroAmount < 1000) {
    return null;
  }

  return (
    <>
      <Grid item xs={12}>
        <Typography variant="overline" color="text.secondary">
          {t("Customer information")}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <FormField label={t("Customer type")}>
          <RadioButtonGroupInput
            source="customer.type"
            label={t("Customer type")}
            choices={[
              { id: "individual", name: t("Individual") },
              { id: "company", name: t("Company") },
            ]}
            validate={required()}
          />
        </FormField>
      </Grid>

      {customerType === "individual" && (
        <>
          <Grid item xs={12} md={6}>
            <FormField label={t("First name")}>
              <TextInput
                source="customer.firstname"
                label={t("First name")}
                fullWidth
                validate={required()}
              />
            </FormField>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormField label={t("Last name")}>
              <TextInput
                source="customer.lastname"
                label={t("Last name")}
                fullWidth
                validate={required()}
              />
            </FormField>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormField label={t("Nationality")}>
              <SelectInput
                source="customer.nationality"
                label={t("Nationality")}
                fullWidth
                validate={required()}
                choices={countries}
              />
            </FormField>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormField label={t("Birth date")}>
              <DateInput
                source="customer.birthDate"
                label={t("Birth date")}
                fullWidth
                validate={required()}
              />
            </FormField>
          </Grid>
        </>
      )}

      {customerType === "company" && (
        <>
          <Grid item xs={12} md={6}>
            <FormField label={t("Legal name")}>
              <TextInput
                source="customer.companyName"
                label={t("Legal name")}
                fullWidth
                validate={required()}
              />
            </FormField>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormField label={t("Country")}>
              <SelectInput
                source="customer.companyCountry"
                label={t("Country")}
                fullWidth
                validate={required()}
                choices={countries}
              />
            </FormField>
          </Grid>

          <Grid item xs={12}>
            <FormField label={t("Address")}>
              <TextInput
                source="customer.companyAddress"
                label={t("Address")}
                fullWidth
                validate={required()}
              />
            </FormField>
          </Grid>
        </>
      )}
    </>
  );
};

const DocumentFields = () => {
  const { t }: { t: any } = useTranslation();
  const { watch } = useFormContext();
  const euroAmount = watch("euroAmount");

  if (!euroAmount || euroAmount < 1000) {
    return null;
  }
  return (
    <Grid item xs={12}>
      <FormField label={t("Documents")}>
        <FileInput
          source="documentOrderIdentity"
          label={t("Identity document")}
          helperText={t("ID card, passport, etc.")}
          accept={"image/*, application/pdf"}
          multiple
          isRequired
        >
          <FileField source="src" title="title" />
        </FileInput>
        <FileInput
          source="documentOrderOther"
          label={t("Other proof")}
          multiple
          helperText={t("Invoice, proof of address, proof of income, etc.")}
          accept={"image/*, application/pdf"}
        >
          <FileField source="src" title="title" />
        </FileInput>
      </FormField>
    </Grid>
  );
};

export const OrderCreate = () => {
  const { t }: { t: any } = useTranslation();
  const { typeProject } = useCurrentProject();
  const projectId = useSyncProjectIdToForm();
  const notify = useNotify();

  const validateCryptoOrder = (values: any) => {
    const errors: any = {};

    if (typeProject !== "cryptoPayment") {
      errors.projectId =
        "Entity selection is not configured to create a crypto payment. Please select another entity on top of the page or update the configuration in the entity settings.";
    }

    // Validation des champs client si le montant >= 1000€
    if (values.euroAmount && values.euroAmount >= 1000) {
      if (!values.customer?.type) {
        if (!errors.customer) errors.customer = {};
        errors.customer.type =
          "Customer type is required for amounts above 1000€";
      }

      if (!values.documentOrderIdentity) {
        if (!errors.documentOrderIdentity) errors.documentOrderIdentity = {};
        errors.documentOrderIdentity.src = "Identity document is required";
      }

      if (!values.documentOrderOther) {
        if (!errors.documentOrderOther) errors.documentOrderOther = {};
        errors.documentOrderOther.src = "At least one document is required";
      }
      // Validation pour les particuliers
      if (values.customer?.type === "individual") {
        if (!errors.customer) errors.customer = {};

        if (!values.customer.firstname) {
          errors.customer.firstname = "First name is required";
        }
        if (!values.customer.lastname) {
          errors.customer.lastname = "Last name is required";
        }
        if (!values.customer.nationality) {
          errors.customer.nationality = "Nationality is required";
        }
        if (!values.customer.birthDate) {
          errors.customer.birthDate = "Birth date is required";
        }
      }

      // Validation pour les entreprises
      if (values.customer?.type === "company") {
        if (!errors.customer) errors.customer = {};

        if (!values.customer.companyName) {
          errors.customer.companyName = "Company name is required";
        }
        if (!values.customer.companyAddress) {
          errors.customer.companyAddress = "Company address is required";
        }
        if (!values.customer.companyCountry) {
          errors.customer.companyCountry = "Company country is required";
        }
      }
    }

    return errors;
  };

  const transformData = async (data: any) => {
    try {
      const documentOrder = [];

      const convertBlobToBase64 = async (blobUrl: string): Promise<string> => {
        try {
          const response = await fetch(blobUrl);
          const blob = await response.blob();
          return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result as string);
            reader.onerror = () =>
              reject(new Error("Erreur lors de la lecture du fichier"));
            reader.readAsDataURL(blob);
          });
        } catch (error) {
          console.error(error);
          throw new Error("Error loading document");
        }
      };

      if (data.documentOrderIdentity) {
        const identityDocs = Array.isArray(data.documentOrderIdentity)
          ? data.documentOrderIdentity
          : [data.documentOrderIdentity];

        for (const document of identityDocs) {
          try {
            const base64Data = await convertBlobToBase64(document.src);
            documentOrder.push({
              ...document,
              src: base64Data,
              type: "identity",
            });
          } catch (error) {
            throw new Error(
              `Error loading identity document: ${error.message}`
            );
          }
        }
      }

      if (data.documentOrderOther) {
        const otherDocs = Array.isArray(data.documentOrderOther)
          ? data.documentOrderOther
          : [data.documentOrderOther];

        for (const document of otherDocs) {
          try {
            const base64Data = await convertBlobToBase64(document.src);
            documentOrder.push({
              ...document,
              src: base64Data,
              type: "other",
            });
          } catch (error) {
            throw new Error(`Error loading document: ${error.message}`);
          }
        }
      }

      const { documentOrderIdentity, documentOrderOther, ...restData } = data;

      return {
        ...restData,
        projectId,
        documentOrder,
      };
    } catch (error) {
      console.error(error);
      notify("Error loading document", { type: "error" });
      throw new Error("Error loading document");
    }
  };

  return (
    <ComponentEdit
      title={t("New transaction")}
      subtitle={t("Create a crypto payment request for a customer.")}
    >
      <Card>
        <Box
          p={3}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box>
            <Typography variant="h6">
              {t("Transaction details")}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t("The amount and currency the customer pays; customer details are required from 1,000 €.")}
            </Typography>
          </Box>
        </Box>
        <Divider />
        <CardContent
          sx={{
            p: 4,
          }}
        >
          <Create redirect="show" transform={transformData}>
            <SimpleForm validate={validateCryptoOrder} toolbar={<Toolbar sx={{ m: 2 }}><SaveButton label={t("Create transaction")} /></Toolbar>}>
              <OrderFormFields />
            </SimpleForm>
          </Create>
        </CardContent>
      </Card>
    </ComponentEdit>
  );
};

export default OrderCreate;
